import { getInformationWithData, getInformationWithDataV3 } from './CABE';
import { getValueCookie } from './cookieService';
import { NumberFormat } from '../functions/generalFunctions';
import { pricesFormat } from '../functions/pricesFormat';

/**
 * salesRoutesService — única puerta de lectura/escritura de las rutas de venta (demo 2026-11-03).
 *
 * Una ruta es una lista de clientes con un nombre y un vendedor asignado.
 *
 * ESTADO ACTUAL
 *  - Rutas: SIN backend. Se guardan en localStorage de este navegador (MOCK).
 *  - Customers y vendedores: endpoints reales de solo lectura para Mayaland (3) y
 *    Altitude (6). Si fallan o vienen vacíos, y siempre en Nebraska (7), datos MOCK
 *    marcados como tales (ver loadCatalogs).
 *  - Autorización de rutas: SIN backend. Por ahora revisa cualquier usuario TypeUser '1'. Lo
 *    guardado en Route Builder queda pendiente; lo guardado en Route Authorization, autorizado.
 *  - Metas por cliente: SIN backend ni pantalla de captura todavía. Se simulan en
 *    todas las compañías (ver loadCustomerGoals).
 *
 * PARA CONECTAR EL BACKEND: buscar "TODO: BACKEND", reemplazar el cuerpo de cada
 * función y borrar todo lo marcado "MOCK". La pantalla solo usa las funciones
 * exportadas, así que no debería cambiar.
 *
 * Forma de una ruta (la misma que debería devolver el backend):
 *   {
 *     id: string,
 *     name: string,
 *     salespersonNo: string,
 *     active: boolean,
 *     customers: [{ customerNo, name, city, state }],
 *     authStatus: 'pending' | 'authorized' | 'rejected',
 *     rejectionComment: string,     // opcional; vacío si no hay
 *     reviewedBy: string,           // vacío mientras esté pendiente
 *     reviewedAt: string ISO | '',
 *     createdBy: string,
 *     createdAt: string ISO,
 *     updatedAt: string ISO
 *   }
 *
 * Convención de respuesta del back: { status: { code: 1 }, data: ... }; code distinto
 * de 1 o un 4xx con { message } se convierten en un Error con ese mensaje. Los Error
 * de validación llevan además .fieldErrors = { name?, salespersonNo?, customers? }.
 */

// Compañías cuyo mirror de AR_Customer existe (arCustomerTable() en el back).
// Nebraska (7) NO está: /ne/v3/salesorder/offline-customers responde, pero lee la tabla
// de Mayaland, y /ne/v2/company/dashboardSalesperson no existe.
const COMPANIES_WITH_REAL_CATALOGS = [3, 6];

// Códigos que no son un vendedor asignable (TOT = pestaña sintética del dashboard).
const NON_ASSIGNABLE_SALESPEOPLE = ['', 'TOT', '0000'];

export const NAME_MAX_LENGTH = 60;
export const REJECTION_COMMENT_MAX_LENGTH = 500;

export const ROUTE_AUTH_STATUS = { pending: 'pending', authorized: 'authorized', rejected: 'rejected' };

const str = (v) => (v === undefined || v === null ? '' : String(v).trim());
const compareText = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export const getCurrentCompanyId = () => (
    getValueCookie('SubCompanyId') ? Number(getValueCookie('SubCompanyId')) : Number(getValueCookie('CompanyId'))
);

const currentUserName = () => str(getValueCookie('userName')).toLowerCase();

// TODO: BACKEND — provisional: se reemplazará por un permiso administrado en Users Management.
//   Esto solo oculta la pantalla; cada endpoint de revisión debe responder 403 si el usuario del
//   token no tiene ese permiso.
export const isRouteSuperAdmin = () => getValueCookie('TypeUser') === '1';

export const salespersonLabel = (salesperson) => {
    if (!salesperson) return '';
    return salesperson.name && salesperson.name !== salesperson.salespersonNo
        ? `${salesperson.name} (${salesperson.salespersonNo})`
        : salesperson.salespersonNo;
};

export const formatMoney = (amount) => `$${NumberFormat(pricesFormat(amount))}`;

// Validación compartida por el formulario y por create/update.
// Devuelve { name?, salespersonNo?, customers? } con el mensaje de cada campo inválido.
// TODO: BACKEND — create y update deben repetir estas reglas y responder 400
//   { status: { code: 0 }, message, fieldErrors: { name?, salespersonNo?, customers? } }.
export const validateRoute = ({ name, salespersonNo, customers }) => {
    const errors = {};
    if (!str(name)) errors.name = 'Enter a route name.';
    else if (str(name).length > NAME_MAX_LENGTH) errors.name = `Use ${NAME_MAX_LENGTH} characters or fewer.`;
    if (!str(salespersonNo)) errors.salespersonNo = 'Assign a salesperson.';
    if (!customers || customers.length === 0) errors.customers = 'Add at least one customer.';
    return errors;
};

const assertValid = (route) => {
    const fieldErrors = validateRoute(route);
    if (Object.keys(fieldErrors).length > 0) {
        const error = new Error('The route has invalid fields.');
        error.fieldErrors = fieldErrors;
        throw error;
    }
};

// ─── Normalización de catálogos ──────────────────────────────────────────────

// Texto en minúsculas precalculado para que filtrar miles de clientes no recorra cada campo.
const withSearchText = (c) => ({
    ...c,
    searchText: [c.customerNo, c.name, c.address, c.city, c.state, c.zipCode, c.phone, c.salespersonNo]
        .join(' ')
        .toLowerCase(),
});

const normalizeCustomers = (rows) => {
    const byNo = new Map();
    rows.forEach((r) => {
        const customerNo = str(r.CustomerNo);
        if (!customerNo || byNo.has(customerNo)) return;
        byNo.set(customerNo, withSearchText({
            customerNo,
            name: str(r.CustomerName) || customerNo,
            address: str(r.AddressLine1),
            city: str(r.City),
            state: str(r.State),
            zipCode: str(r.ZipCode),
            phone: str(r.TelephoneNo),
            salespersonNo: str(r.SalespersonNo),
        }));
    });
    return Array.from(byNo.values());
};

// Une los usuarios del dashboard (traen nombre) con los códigos que usan los clientes
// (dashboardSalesperson solo trae inDashboard = 1, así que puede omitir vendedores).
const normalizeSalespeople = (dashboardRows, customers) => {
    const byNo = new Map();
    dashboardRows.forEach((u) => {
        const salespersonNo = str(u.salesPerson);
        if (NON_ASSIGNABLE_SALESPEOPLE.includes(salespersonNo) || byNo.has(salespersonNo)) return;
        byNo.set(salespersonNo, { salespersonNo, name: str(u.userName) || salespersonNo });
    });
    customers.forEach((c) => {
        if (NON_ASSIGNABLE_SALESPEOPLE.includes(c.salespersonNo) || byNo.has(c.salespersonNo)) return;
        byNo.set(c.salespersonNo, { salespersonNo: c.salespersonNo, name: c.salespersonNo });
    });
    return Array.from(byNo.values()).sort((a, b) => compareText(a.name, b.name));
};

// ─── MOCK: catálogos de ejemplo (borrar al conectar Nebraska) ────────────────

const MOCK_NAME_A = ['Maya', 'Sunrise', 'La Esperanza', 'El Sol', 'Golden', 'Fresh', 'Plaza', 'Central', 'Los Amigos', 'Valley'];
const MOCK_NAME_B = ['Market', 'Grocery', 'Foods', 'Supermarket', 'Mini Mart', 'Carniceria', 'Tienda', 'Produce'];
const MOCK_PLACES = [
    ['Omaha', 'NE'], ['Lincoln', 'NE'], ['Grand Island', 'NE'], ['Kearney', 'NE'],
    ['Atlanta', 'GA'], ['Marietta', 'GA'], ['Orlando', 'FL'], ['Tampa', 'FL'],
];

const MOCK_SALESPEOPLE = [
    { salespersonNo: 'MK01', name: 'Sample Rep A' },
    { salespersonNo: 'MK02', name: 'Sample Rep B' },
    { salespersonNo: 'MK03', name: 'Sample Rep C' },
    { salespersonNo: 'MK04', name: 'Sample Rep D' },
];

// 800 clientes para que la demo también muestre búsqueda y paginación con volumen.
const buildMockCustomers = () => {
    const rows = [];
    for (let i = 1; i <= 800; i += 1) {
        const place = MOCK_PLACES[i % MOCK_PLACES.length];
        rows.push({
            CustomerNo: `MOCK${String(i).padStart(4, '0')}`,
            CustomerName: `[SAMPLE] ${MOCK_NAME_A[i % MOCK_NAME_A.length]} ${MOCK_NAME_B[i % MOCK_NAME_B.length]} ${i}`,
            AddressLine1: `${100 + i} Sample St`,
            City: place[0],
            State: place[1],
            ZipCode: String(10000 + i),
            TelephoneNo: `555-${String(1000 + i)}`,
            SalespersonNo: MOCK_SALESPEOPLE[i % MOCK_SALESPEOPLE.length].salespersonNo,
        });
    }
    return normalizeCustomers(rows);
};

// ─── MOCK: almacenamiento de rutas en localStorage ───────────────────────────

// Las rutas armadas con clientes de ejemplo no se mezclan con las de clientes reales:
// si el catálogo real falla un día, la demo usa otra llave y las reales siguen intactas.
let mockStorageScope = 'live';
const mockStorageKey = (idCompany) => `salesRoutes:demo:v1:${idCompany}:${mockStorageScope}`;

const mockDelay = () => new Promise((resolve) => setTimeout(resolve, 150));

const mockRead = (idCompany) => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(mockStorageKey(idCompany)));
        if (!Array.isArray(parsed)) return [];
        // Las rutas guardadas antes de existir la autorización entran como pendientes.
        return parsed
            .filter((r) => r && r.id && Array.isArray(r.customers))
            .map((r) => ({
                authStatus: ROUTE_AUTH_STATUS.pending,
                rejectionComment: '',
                reviewedBy: '',
                reviewedAt: '',
                createdBy: '',
                createdAt: r.updatedAt || '',
                ...r,
            }));
    } catch (e) {
        return [];
    }
};

const mockWrite = (idCompany, routes) => {
    try {
        window.localStorage.setItem(mockStorageKey(idCompany), JSON.stringify(routes));
    } catch (e) {
        throw new Error('This browser could not save the route.');
    }
};

const mockReplace = (idCompany, routeId, changes) => {
    const routes = mockRead(idCompany);
    const index = routes.findIndex((r) => r.id === routeId);
    if (index === -1) throw new Error('This route no longer exists.');
    routes[index] = { ...routes[index], ...changes, updatedAt: new Date().toISOString() };
    mockWrite(idCompany, routes);
    return routes[index];
};

// Lo guardado desde la pantalla de autorización queda autorizado; lo demás vuelve a revisión.
const mockReviewFields = (autoAuthorize) => (autoAuthorize
    ? { authStatus: ROUTE_AUTH_STATUS.authorized, rejectionComment: '', reviewedBy: currentUserName(), reviewedAt: new Date().toISOString() }
    : { authStatus: ROUTE_AUTH_STATUS.pending, rejectionComment: '', reviewedBy: '', reviewedAt: '' });

const toRouteCustomer = (c) => ({ customerNo: c.customerNo, name: c.name, city: c.city, state: c.state });

const toRouteFields = ({ name, salespersonNo, customers }) => ({
    name: str(name),
    salespersonNo: str(salespersonNo),
    customers: customers.map(toRouteCustomer),
});

// Primera visita: arma rutas de ejemplo con los clientes cargados, para que la pantalla
// no se vea vacía en la demo. Se agrupa por vendedor y ciudad, y hay una en cada estado de revisión.
const mockSeedRoutesIfNeeded = (idCompany, customers, salespeople) => {
    if (window.localStorage.getItem(mockStorageKey(idCompany)) !== null) return;
    if (customers.length === 0 || salespeople.length === 0) return;

    const groups = new Map();
    customers.forEach((c) => {
        if (!c.city || !salespeople.some((s) => s.salespersonNo === c.salespersonNo)) return;
        const key = `${c.salespersonNo}|${c.city}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(c);
    });

    const usedSalespeople = new Set();
    const picked = Array.from(groups.values())
        .sort((a, b) => b.length - a.length)
        .filter((list) => {
            if (usedSalespeople.has(list[0].salespersonNo)) return false;
            usedSalespeople.add(list[0].salespersonNo);
            return true;
        })
        .slice(0, 3);

    const now = new Date().toISOString();
    const sampleReviews = [
        { authStatus: ROUTE_AUTH_STATUS.pending, rejectionComment: '', reviewedBy: '', reviewedAt: '' },
        { authStatus: ROUTE_AUTH_STATUS.authorized, rejectionComment: '', reviewedBy: 'sample.reviewer', reviewedAt: now },
        { authStatus: ROUTE_AUTH_STATUS.rejected, rejectionComment: 'Sample comment: please review the customers on this route.', reviewedBy: 'sample.reviewer', reviewedAt: now },
    ];
    mockWrite(idCompany, picked.map((list, i) => ({
        id: `route-sample-${i + 1}`,
        ...toRouteFields({
            name: `${list[0].city} Route`,
            salespersonNo: list[0].salespersonNo,
            customers: list.slice(0, 15),
        }),
        active: true,
        ...sampleReviews[i],
        createdBy: 'sample.admin',
        createdAt: now,
        updatedAt: now,
    })));
};

// ─── Catálogos (lectura) ─────────────────────────────────────────────────────

const fetchRealCustomers = async (idCompany) => {
    const resp = await getInformationWithDataV3('/v3/salesorder/offline-customers', {
        idCompany,
        salespersonNo: getValueCookie('SalesPerson'),
        idTypeofUser: getValueCookie('TypeUser'),
    });
    if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) return normalizeCustomers(resp.data);
    return [];
};

const fetchDashboardSalespeople = async (idCompany) => {
    const resp = await getInformationWithData('/v2/company/dashboardSalesperson', { companyId: idCompany });
    return resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data) ? resp.data : [];
};

// Carga customers y vendedores. Devuelve:
//   { customers: [...], salespeople: [...], customersSource, salespeopleSource }
// donde cada source es 'real' o 'mock', para que la pantalla lo muestre.
//
// TODO: BACKEND — los endpoints reales ya existen para Mayaland y Altitude:
//   POST {apiPrefix}/v3/salesorder/offline-customers
//     body { data: { idCompany, salespersonNo, idTypeofUser } }
//     resp { status, data: [{ CustomerNo, CustomerName, AddressLine1, City, State, ZipCode, TelephoneNo, SalespersonNo, ... }] }
//   POST {apiPrefix}/v2/company/dashboardSalesperson
//     body { data: { companyId } }
//     resp { status, data: [{ userName, idTypeofUser, salesPerson }] }
//   Falta Nebraska: que arCustomerTable() atienda la compañía 7 y agregar dashboardSalesperson
//   a routes_ne. Con eso, agregar 7 a COMPANIES_WITH_REAL_CATALOGS y borrar lo marcado MOCK.
export const loadCatalogs = async (idCompany) => {
    let customers = [];
    let dashboardRows = [];

    if (COMPANIES_WITH_REAL_CATALOGS.includes(idCompany)) {
        const [customersResult, salespeopleResult] = await Promise.all([
            fetchRealCustomers(idCompany).catch(() => []),
            fetchDashboardSalespeople(idCompany).catch(() => []),
        ]);
        customers = customersResult;
        dashboardRows = salespeopleResult;
    }

    const customersSource = customers.length > 0 ? 'real' : 'mock';
    if (customersSource === 'mock') customers = buildMockCustomers();

    let salespeople = normalizeSalespeople(dashboardRows, customersSource === 'real' ? customers : []);
    const salespeopleSource = salespeople.length > 0 ? 'real' : 'mock';
    if (salespeopleSource === 'mock') salespeople = MOCK_SALESPEOPLE;

    // MOCK: quitar estas dos líneas al conectar las rutas.
    mockStorageScope = customersSource === 'real' ? 'live' : 'mock';
    try {
        mockSeedRoutesIfNeeded(idCompany, customers, salespeople);
    } catch (e) {
        // Sin localStorage la pantalla igual carga; el error sale al intentar guardar.
        console.error('Could not create the sample routes:', e);
    }

    return { customers, salespeople, customersSource, salespeopleSource };
};

// ─── Metas por cliente y totales ─────────────────────────────────────────────

// Solo una meta numérica mayor que cero cuenta; lo demás se trata como "sin meta".
const normalizeGoals = (rows) => {
    const goals = new Map();
    rows.forEach((r) => {
        const customerNo = str(r.customerNo);
        const goal = Number(r.goal);
        if (customerNo && Number.isFinite(goal) && goal > 0) goals.set(customerNo, goal);
    });
    return goals;
};

// MOCK: alrededor de 4 de cada 10 clientes tienen meta, entre $500 y $12,000. Sale del número
// de cliente para que cada uno conserve la misma meta al recargar.
const buildMockGoalRows = (customers) => customers.reduce((rows, c) => {
    let hash = 0;
    for (let i = 0; i < c.customerNo.length; i += 1) hash = (hash * 31 + c.customerNo.charCodeAt(i)) % 1000003;
    if (hash % 100 < 40) rows.push({ customerNo: c.customerNo, goal: 500 + ((hash % 47) * 250) });
    return rows;
}, []);

// Metas de venta por cliente. Devuelve { goals: Map(customerNo → monto), source: 'mock' | 'real' }.
// Un cliente sin meta no viene en el Map, y la pantalla no muestra nada para él.
//
// TODO: BACKEND — POST {apiPrefix}/v3/sales-routes/customer-goals
//   body { data: { idCompany } }
//   resp { status: { code: 1 }, data: [{ customerNo: string, goal: number }] }  (solo clientes con meta)
//   La captura de metas será otra pantalla. Al conectar, quitar el parámetro customers.
export const loadCustomerGoals = async (idCompany, customers) => {
    await mockDelay();
    return { goals: normalizeGoals(buildMockGoalRows(customers)), source: 'mock' };
};

const roundMoney = (amount) => Math.round(amount * 100) / 100;

// Total esperado de una lista de clientes: { expected, withGoal }.
// TODO: BACKEND — hoy se calcula en el front con loadCustomerGoals; no necesita endpoint propio
//   salvo que list devuelva ya el total por ruta.
export const summarizeCustomerGoals = (customers, goals) => {
    let expected = 0;
    let withGoal = 0;
    customers.forEach((c) => {
        const goal = goals.get(c.customerNo);
        if (goal) {
            expected += goal;
            withGoal += 1;
        }
    });
    return { expected: roundMoney(expected), withGoal };
};

// Una ruta solo suma a los totales si está activa y autorizada.
export const isRouteCounted = (route) => route.active && route.authStatus === ROUTE_AUTH_STATUS.authorized;

// Totales generales de las rutas que cuentan. Un cliente en varias rutas cuenta una sola vez.
// TODO: BACKEND — se calcula en el front; ver summarizeBySalesperson si se mueve al back.
export const summarizeCountedRoutes = (routes, goals) => {
    const customers = new Map();
    routes.forEach((r) => {
        if (isRouteCounted(r)) r.customers.forEach((c) => customers.set(c.customerNo, c));
    });
    return { customers: customers.size, ...summarizeCustomerGoals(Array.from(customers.values()), goals) };
};

// Resumen por vendedor, con la meta más alta primero. Solo suman las rutas activas y autorizadas;
// las demás se cuentan aparte. Un cliente repetido en rutas del mismo vendedor suma una sola vez.
//
// TODO: BACKEND — opcional si el resumen se mueve al back: POST {apiPrefix}/v3/sales-routes/summary-by-salesperson
//   body { data: { idCompany } }
//   resp { status: { code: 1 }, data: [{ salespersonNo, countedRoutes, pendingRoutes, rejectedRoutes, inactiveRoutes, customers, withGoal, expected, repeatedCustomers }] }
export const summarizeBySalesperson = (routes, goals) => {
    const bySalesperson = new Map();
    routes.forEach((r) => {
        if (!bySalesperson.has(r.salespersonNo)) {
            bySalesperson.set(r.salespersonNo, { countedRoutes: 0, pendingRoutes: 0, rejectedRoutes: 0, inactiveRoutes: 0, assignments: 0, customers: new Map() });
        }
        const entry = bySalesperson.get(r.salespersonNo);
        if (r.authStatus === ROUTE_AUTH_STATUS.pending) entry.pendingRoutes += 1;
        else if (r.authStatus === ROUTE_AUTH_STATUS.rejected) entry.rejectedRoutes += 1;
        else if (!r.active) entry.inactiveRoutes += 1;
        else {
            entry.countedRoutes += 1;
            entry.assignments += r.customers.length;
            r.customers.forEach((c) => entry.customers.set(c.customerNo, c));
        }
    });
    return Array.from(bySalesperson.entries())
        .map(([salespersonNo, entry]) => ({
            salespersonNo,
            countedRoutes: entry.countedRoutes,
            pendingRoutes: entry.pendingRoutes,
            rejectedRoutes: entry.rejectedRoutes,
            inactiveRoutes: entry.inactiveRoutes,
            customers: entry.customers.size,
            repeatedCustomers: entry.assignments - entry.customers.size,
            ...summarizeCustomerGoals(Array.from(entry.customers.values()), goals),
        }))
        .sort((a, b) => b.expected - a.expected || compareText(a.salespersonNo, b.salespersonNo));
};

// ─── Rutas (MOCK en localStorage) ────────────────────────────────────────────

// Lista todas las rutas de la compañía, activas e inactivas.
//
// TODO: BACKEND — POST {apiPrefix}/v3/sales-routes/list
//   body { data: { idCompany } }
//   resp { status: { code: 1 }, data: [Route] }  (ver forma de Route arriba del archivo)
export const listRoutes = async (idCompany) => {
    await mockDelay();
    return mockRead(idCompany);
};

// Crea una ruta, activa por defecto. route = { name, salespersonNo, customers: [{ customerNo, ... }] }.
// Queda pendiente de revisión, salvo que se cree desde la pantalla de autorización
// (options.autoAuthorize).
//
// TODO: BACKEND — POST {apiPrefix}/v3/sales-routes/create
//   body { data: { idCompany, name, salespersonNo, customerNos: route.customers.map(c => c.customerNo), autoAuthorize: boolean } }
//   resp { status: { code: 1 }, data: Route }; 400 con fieldErrors si no pasa validateRoute;
//   403 si pide autoAuthorize sin permiso de revisión. El back asigna createdBy y createdAt del token.
export const createRoute = async (idCompany, route, options = {}) => {
    assertValid(route);
    await mockDelay();
    const now = new Date().toISOString();
    const created = {
        id: `route-${Date.now()}`,
        ...toRouteFields(route),
        active: true,
        ...mockReviewFields(options.autoAuthorize),
        createdBy: currentUserName(),
        createdAt: now,
        updatedAt: now,
    };
    mockWrite(idCompany, [...mockRead(idCompany), created]);
    return created;
};

// Guarda nombre, vendedor y clientes de una ruta existente. No cambia si está activa, pero
// vuelve a revisión y borra el comentario anterior, salvo con options.autoAuthorize.
//
// TODO: BACKEND — POST {apiPrefix}/v3/sales-routes/update
//   body { data: { idCompany, routeId, name, salespersonNo, customerNos: route.customers.map(c => c.customerNo), autoAuthorize: boolean } }
//   resp { status: { code: 1 }, data: Route }; 400 con fieldErrors; 403 si pide autoAuthorize
//   sin permiso de revisión; 404 si la ruta no existe
export const updateRoute = async (idCompany, routeId, route, options = {}) => {
    assertValid(route);
    await mockDelay();
    return mockReplace(idCompany, routeId, { ...toRouteFields(route), ...mockReviewFields(options.autoAuthorize) });
};

// Mueve la ruta a otro vendedor sin tocar sus clientes. Es una edición: vuelve a revisión,
// salvo con options.autoAuthorize.
//
// TODO: BACKEND — POST {apiPrefix}/v3/sales-routes/set-salesperson
//   body { data: { idCompany, routeId, salespersonNo, autoAuthorize: boolean } }
//   resp { status: { code: 1 }, data: Route }; 400 si falta salespersonNo; 403 si pide autoAuthorize
//   sin permiso de revisión; 404 si la ruta no existe
export const setRouteSalesperson = async (idCompany, routeId, salespersonNo, options = {}) => {
    if (!str(salespersonNo)) throw new Error('Assign a salesperson.');
    await mockDelay();
    return mockReplace(idCompany, routeId, { salespersonNo: str(salespersonNo), ...mockReviewFields(options.autoAuthorize) });
};

// Activa o desactiva una ruta. No cambia su estado de revisión.
//
// TODO: BACKEND — POST {apiPrefix}/v3/sales-routes/set-active
//   body { data: { idCompany, routeId, active: boolean } }
//   resp { status: { code: 1 }, data: Route }; 404 si la ruta no existe
export const setRouteActive = async (idCompany, routeId, active) => {
    await mockDelay();
    return mockReplace(idCompany, routeId, { active: Boolean(active) });
};

// ─── Autorización ────────────────────────────────────────────────────────────

const assertCanReview = () => {
    if (!isRouteSuperAdmin()) throw new Error('You do not have permission to review routes.');
};

// Autoriza una ruta. También sirve para autorizar una que se había rechazado.
//
// TODO: BACKEND — POST {apiPrefix}/v3/sales-routes/authorize
//   body { data: { idCompany, routeId } }
//   resp { status: { code: 1 }, data: Route }; 403 si no es super admin; 404 si la ruta no existe
//   El back asigna reviewedBy y reviewedAt a partir del usuario del token.
export const authorizeRoute = async (idCompany, routeId) => {
    assertCanReview();
    await mockDelay();
    return mockReplace(idCompany, routeId, {
        authStatus: ROUTE_AUTH_STATUS.authorized,
        rejectionComment: '',
        reviewedBy: currentUserName(),
        reviewedAt: new Date().toISOString(),
    });
};

// Rechaza una ruta. El comentario es opcional.
//
// TODO: BACKEND — POST {apiPrefix}/v3/sales-routes/reject
//   body { data: { idCompany, routeId, comment: string } }  (comment puede ir vacío; máx. 500 caracteres)
//   resp { status: { code: 1 }, data: Route }; 400 si el comentario excede el límite; 403 si no es
//   super admin; 404 si la ruta no existe. El back asigna reviewedBy y reviewedAt a partir del token.
export const rejectRoute = async (idCompany, routeId, comment) => {
    assertCanReview();
    if (str(comment).length > REJECTION_COMMENT_MAX_LENGTH) {
        throw new Error(`Use ${REJECTION_COMMENT_MAX_LENGTH} characters or fewer.`);
    }
    await mockDelay();
    return mockReplace(idCompany, routeId, {
        authStatus: ROUTE_AUTH_STATUS.rejected,
        rejectionComment: str(comment),
        reviewedBy: currentUserName(),
        reviewedAt: new Date().toISOString(),
    });
};

// Avisa cuando las rutas cambian fuera de esta pantalla, para refrescarlas sin recargar.
// Devuelve la función para dejar de escuchar. MOCK: solo detecta cambios de otra pestaña del
// mismo navegador, porque las rutas viven en su localStorage.
//
// TODO: BACKEND — sustituir por consultas periódicas a /v3/sales-routes/list (por ejemplo cada
//   30 s) o por un canal en tiempo real, para que el admin vea las decisiones del super admin.
export const subscribeToRouteChanges = (idCompany, onChange) => {
    const onStorage = (e) => {
        if (e.key === mockStorageKey(idCompany)) onChange();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
};
